import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  billingSessionService,
  BillingSession,
} from "../../services/billing-session.service";
import { useAuthStore } from "../../stores/authStore";
import { useBillingSessionStore } from "../../stores/billingSessionStore";
import {
  OpenSessionModal,
  CloseSessionModal,
} from "../../components/billing-session/SessionModals";
import { showToast } from "../../lib/toast";
import { formatDate, formatTime } from "../../lib/utils";
import { storeService } from "../../services/store.service";

const STATUS_COLORS: Record<string, string> = {
  OPEN: "bg-green-100 text-green-800",
  CLOSED: "bg-gray-100 text-gray-800",
  RECONCILED: "bg-blue-100 text-blue-800",
};

export function BillingSessionsPage() {
  const navigate = useNavigate();
  const { selectedStoreId, selectedCounterId } = useAuthStore();

  // Use global billing session store for active session
  const {
    activeSession,
    fetchActiveSession,
    showOpenSessionModal,
    showCloseSessionModal,
    setShowOpenSessionModal,
    setShowCloseSessionModal,
  } = useBillingSessionStore();

  const [sessions, setSessions] = useState<BillingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [storeName, setStoreName] = useState("");
  const limit = 20;

  // Load sessions when filters change or when activeSession changes (after open/close)
  useEffect(() => {
    loadSessions();
  }, [page, statusFilter, selectedStoreId, selectedCounterId, activeSession]);

  // Refresh active session on mount and when store/counter changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchActiveSession(selectedStoreId, selectedCounterId || undefined);
      // Load store name for modal
      loadStoreName();
    }
  }, [selectedStoreId, selectedCounterId]);

  const loadStoreName = async () => {
    if (!selectedStoreId) return;
    try {
      const stores = await storeService.getAll();
      const store = stores.find((s) => s.id === selectedStoreId);
      setStoreName(store?.name || "");
    } catch {
      setStoreName("");
    }
  };

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await billingSessionService.getAll({
        page,
        limit,
        storeId: selectedStoreId || undefined,
        counterId: selectedCounterId || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      setSessions(response.data || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load billing sessions",
      );
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const totalPages = Math.ceil(total / limit);

  const handleSessionOpened = (storeId: string, counterId: string) => {
    fetchActiveSession(storeId, counterId);
  };

  return (
    <div className="p-6">
      {/* Session Modals */}
      <OpenSessionModal
        isOpen={showOpenSessionModal}
        onClose={() => setShowOpenSessionModal(false)}
        storeId={selectedStoreId || ""}
        storeName={storeName}
        onSessionOpened={handleSessionOpened}
      />
      <CloseSessionModal
        isOpen={showCloseSessionModal}
        onClose={() => setShowCloseSessionModal(false)}
        onSessionClosed={() => {}}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Sessions</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage daily shifts and cash drawer
          </p>
        </div>
        {!activeSession ? (
          <button
            onClick={() => setShowOpenSessionModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Open New Session
          </button>
        ) : (
          <button
            onClick={() => setShowCloseSessionModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Close Active Session
          </button>
        )}
      </div>

      {/* Active Session Card */}
      {activeSession && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-800">
                Active Session: {activeSession.sessionNumber}
              </h3>
              <p className="text-sm text-green-600">
                {activeSession.shift} shift • Started at{" "}
                {formatTime(activeSession.startTime)}
                {activeSession.counter?.name
                  ? ` • Counter: ${activeSession.counter.name}`
                  : ""}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-green-600">Today's Sales</div>
              <div className="text-xl font-bold text-green-800">
                {formatCurrency(activeSession.totalSales)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
            <option value="RECONCILED">Reconciled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No billing sessions found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Counter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shift
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opened By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoices
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {session.sessionNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(session.sessionDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.store?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.counter?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.shift}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTime(session.startTime)}
                    {session.endTime && <> - {formatTime(session.endTime)}</>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.openedByName || session.openedBy}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(session.totalSales)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {session.invoiceCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        STATUS_COLORS[session.status] || "bg-gray-100"
                      }`}
                    >
                      {session.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() =>
                        navigate(`/billing-sessions/${session.id}`)
                      }
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
            <div className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingSessionsPage;
